<template>
  <div class="cs" :class="'is-' + m.req_status">
    <div class="cs-top">
      <!-- Connect's mark: the sparkle on the brand gradient -->
      <span class="cs-tile" aria-hidden="true">
        <svg viewBox="0 0 24 24"><path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" /><path d="M19 17l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z" /></svg>
      </span>
      <StatusBadge :label="label" :tone="tone" />
      <span class="cs-when">{{ when }}</span>
    </div>
    <div class="cs-title">{{ title }}</div>
    <div class="cs-sum">{{ m.body }}</div>

    <div v-if="isSync && progress && progress.total" class="cs-progress">
      <div class="cs-bar"><i :style="{ width: pct + '%' }"></i></div>
      <span>{{ progress.stores_done }} of {{ progress.stores }} stores<template v-if="progress.failed"> · {{ $trf("{0} failed", [progress.failed]) }}</template></span>
    </div>
    <div v-else-if="countLine" class="cs-counts">{{ countLine }}</div>

    <button v-if="lines" type="button" class="cs-link" @click="open = !open">
      {{ open ? $tr("Hide the list") : $trf("Review every change ({0})", [lines]) }}
    </button>
    <div v-if="open && view" class="cs-review">
      <div v-for="(g, i) in view.groups" :key="'g' + i" class="cs-group">
        <div class="cs-g-head">
          <b>{{ g.label }}</b>
          <span v-if="g.from" class="cs-g-arrow">{{ g.from }} → {{ g.to }}</span>
          <span v-if="storeCount(g)" class="cs-g-n">{{ storeCount(g) === 1 ? $tr("1 store") : $trf("{0} stores", [storeCount(g)]) }}</span>
        </div>
        <div class="cs-stores">
          <span v-for="(s, j) in g.stores" :key="j" class="cs-store" :class="'st-' + s.status" :title="s.error ? s.status + ': ' + s.error : s.status">{{ s.name }}</span>
        </div>
      </div>
      <div v-for="(n, i) in view.notes" :key="'n' + i" class="cs-note">
        {{ n.label }}: {{ n.why }}<template v-if="n.stores.length"> — {{ n.stores.join(", ") }}</template>
      </div>
    </div>

    <DangerConfirm v-if="m.req_status === 'pending' && mine && danger" :count="danger" :busy="busy"
                   @confirm="typed => $emit('decide', m, 'confirm_delete', typed)" @cancel="$emit('decide', m, 'cancel')" />
    <div class="cs-actions" v-else-if="m.req_status === 'pending' && mine">
      <button type="button" class="cs-btn cs-btn--go" :disabled="busy" @click="$emit('decide', m, 'confirm')">{{ needsApproval ? "Send for approval" : "Confirm" }}</button>
      <button v-if="canTrial" type="button" class="cs-btn" :disabled="busy" title="Apply it at the store selected in the panel (or the first one), check it, then apply the rest" @click="$emit('decide', m, 'confirm_one')">Try at one store first</button>
      <button type="button" class="cs-btn" :disabled="busy" @click="$emit('decide', m, 'cancel')">Cancel</button>
    </div>
    <div class="cs-sum" v-else-if="m.req_status === 'pending'">Waiting for the person who asked.</div>

    <div class="cs-actions" v-if="mine && view && view.status === 'trial'">
      <button type="button" class="cs-btn cs-btn--go" :disabled="working" @click="applyRest">
        Apply the rest: {{ where(view.waiting_stores, view.waiting_shared) }}
      </button>
    </div>

    <div class="cs-actions" v-if="mine && isSync && m.req_status === 'working' && !stepping">
      <button type="button" class="cs-btn cs-btn--go" @click="runSync">Continue the sync</button>
    </div>
    <div class="cs-actions" v-if="mine && isSync && view && (view.status === 'partial' || view.status === 'failed')">
      <button type="button" class="cs-btn" :disabled="stepping" @click="retry">Retry the failed stores</button>
    </div>
    <div class="cs-actions" v-if="mine && !isSync && view && (view.status === 'applied' || view.status === 'partial')">
      <button v-if="view.can_sync && !view.followups.sync" type="button" class="cs-btn cs-btn--go" :disabled="working" @click="followUp('sync')">Prepare the sync</button>
      <button v-if="view.can_undo && canUndoAgain && view.type !== 'undo'" type="button" class="cs-btn" :disabled="working" @click="followUp('undo')">Undo</button>
    </div>
  </div>
</template>

<script>
import DangerConfirm from "./DangerConfirm.vue";
import { locale, tr, trf } from "@/i18n";

/** What a set is about, as the server names it (MenuOps catalog), in the panel's language; another name as it comes. */
const subjectWord = (subject) =>
  ({
    menu: tr("menu"), banner: tr("banner"), coupon: tr("coupon"), deal: tr("deal"), hours: tr("hours"),
    notification: tr("notification"), role: tr("role"), "till user": tr("till user"), user: tr("user"), zone: tr("zone"),
  })[subject] || subject;

/**
 * A change-set card in the assistant's chat (server: classes/MenuOps): a menu, a coupon, a
 * deal or a banner change. The chat row is the card; what it holds lives in its own set,
 * fetched here: every change grouped the way a person reads it, each store's result, and the
 * follow-ups (sync, for a menu change; undo).
 *
 * A sync is driven from here, one store per request, so nothing on the server runs long.
 * It starts by itself right after Confirm; after a reload the card offers "Continue".
 */
export default {
  name: "ChangeSetCard",
  components: { DangerConfirm },
  props: {
    m: { type: Object, required: true },
    mine: { type: Boolean, default: false },
    when: { type: String, default: "" },
    busy: { type: Boolean, default: false },
  },
  emits: ["decide"],
  data() {
    return { view: null, open: false, stepping: false, working: false };
  },
  computed: {
    cs() { return Number((this.m.proposal && this.m.proposal.args && this.m.proposal.args.cs) || 0); },
    /** How many things the set deletes or takes away: the number the person types (DangerConfirm). */
    danger() { return Number((this.m.proposal && this.m.proposal.args && this.m.proposal.args._confirm) || 0); },
    tool() { return (this.m.proposal && this.m.proposal.tool) || ""; },
    isSync() { return this.tool === "menu_sync"; },
    /** This person's role sends what they confirm to an administrator first (assistant.needs_approval). */
    needsApproval() { return this.$store.state.assistant.needsApproval; },
    /** "Menu change", "Undo a coupon change", "Sync to the tills": what the set is about comes with it (view.subject). */
    title() {
      if (this.isSync) return tr("Sync to the tills");
      const subject = subjectWord((this.view && this.view.subject) || "menu");
      if (this.tool === "menu_undo" || this.tool === "undo_change") return trf("Undo a {0} change", [subject]);
      return trf("{0} change", [subject.charAt(0).toLocaleUpperCase(locale.value === "tr" ? "tr-TR" : "en") + subject.slice(1)]);
    },
    progress() { return this.view ? this.view.progress : null; },
    pct() {
      const p = this.progress;
      return p && p.total ? Math.round(((p.done + p.failed + p.skipped) / p.total) * 100) : 0;
    },
    lines() { return this.view ? this.view.groups.reduce((t, g) => t + g.stores.length, 0) : 0; },
    countLine() {
      if (!this.view) return "";
      const c = this.view.counts || {};
      const parts = [];
      if (c.planned) parts.push(trf("{0} to do", [c.planned]));
      if (c.applied) parts.push(trf("{0} applied", [c.applied]));
      if (c.conflict) parts.push(trf("{0} not changed, edited since", [c.conflict]));
      if (c.failed) parts.push(trf("{0} failed", [c.failed]));
      if (!parts.length) return "";
      const where = this.where(this.view.stores, this.view.shared);
      return parts.join(" · ") + (where ? " — " + where : "");
    },
    /** No undo yet, or one that went only part of the way (the server allows another). */
    canUndoAgain() {
      const u = this.view && this.view.followups.undo;
      return !u || u.status === "partial" || u.status === "failed";
    },
    /**
     * "Try at one store first" is for a menu change spanning several stores (what every store
     * shares waits with the rest, it is never the trial); a sync, an undo or any other change runs whole.
     */
    canTrial() {
      const v = this.view;
      return !!v && !this.needsApproval && v.type === "change" && v.subject === "menu" && v.stores >= 1 && v.stores + (v.shared ? 1 : 0) > 1;
    },
    label() {
      const s = this.m.req_status;
      if (this.view && this.view.status === "trial") return tr("Tried at one store");
      if (s === "pending") return tr("Needs your confirmation");
      if (s === "awaiting") return tr("Waiting for an administrator");
      if (s === "rejected") return tr("Not approved");
      if (s === "working") return this.isSync ? (this.stepping ? tr("Syncing") : tr("Sync paused")) : tr("Working");
      if (s === "done") return this.isSync ? tr("Synced") : tr("Done");
      if (s === "partial") return tr("Partly done");
      if (s === "failed") return tr("Failed");
      if (s === "cancelled") return tr("Cancelled");
      if (s === "expired") return tr("Expired");
      return s;
    },
    tone() {
      const s = this.m.req_status;
      if (s === "done") return "green";
      if (s === "failed" || s === "rejected") return "red";
      if (s === "cancelled" || s === "expired") return "gray";
      if (s === "working" && this.stepping) return "blue";
      return "amber";
    },
  },
  watch: {
    "m.req_status"(now, before) {
      this.load();
      // Confirmed here, just now: run it. After a reload a running sync waits for "Continue".
      if (this.isSync && this.mine && before === "pending" && now === "working") this.runSync();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    /** "3 stores", "3 stores and what every store shares" (the topping list, the brand menus), or just that. */
    where(stores, shared) {
      const parts = [];
      if (stores) parts.push(stores === 1 ? tr("1 store") : trf("{0} stores", [stores]));
      if (shared) parts.push(tr("what every store shares"));
      return parts.length === 2 ? trf("{0} and {1}", parts) : parts.join(" and ");
    },
    /** Real stores in a group of the review; what every store shares shows as its own chip. */
    storeCount(g) {
      return g.stores.filter((s) => s.sid > 0).length;
    },
    async load() {
      if (!this.cs) return;
      const v = await this.$store.dispatch("assistantChangeset", this.cs);
      if (v) this.view = v;
    },
    async runSync() {
      if (this.stepping) return;
      this.stepping = true;
      try {
        for (let i = 0; i < 200; i++) {
          const r = await this.$store.dispatch("assistantChangesetStep", this.cs);
          if (!r || !r.status || !r.data) break;
          if (this.view) this.view = { ...this.view, status: r.data.status, progress: r.data.progress };
          if (r.data.status !== "syncing") break;
          // Another tab holds this sync's lock: let it work, look again shortly.
          if (r.data.busy) await new Promise((res) => setTimeout(res, 1500));
        }
      } finally {
        this.stepping = false;
        await this.load();
        this.$store.dispatch("chatPoll");
      }
    },
    async retry() {
      const r = await this.$store.dispatch("assistantChangesetRetry", this.cs);
      if (!r || !r.status) return;
      this.$store.commit("PATCH_CHAT_MESSAGE", { id: this.m.id, patch: { req_status: "working" } });
      await this.load();
      this.runSync();
    },
    async followUp(what) {
      this.working = true;
      await this.$store.dispatch("assistantChangesetFollowUp", { id: this.cs, what });
      this.working = false;
      this.load();
    },
    async applyRest() {
      this.working = true;
      await this.$store.dispatch("assistantChangesetContinue", this.cs);
      this.working = false;
      this.load();
    },
  },
};
</script>

<style scoped>
/* A card in the drawer's look (RightDrawer): icon tile, tinted pill, pill buttons. A card that waits for
   the person has the brand edge; the progress bar fills without motion. */
.cs { align-self: stretch; padding: var(--s-4); background: var(--bg-primary); border: 1px solid var(--border-primary); border-radius: var(--card-radius, 16px); box-shadow: var(--card-shadow); }
.cs.is-pending { border-color: var(--accent-primary); }
.cs.is-partial { border-color: var(--warning); }
.cs.is-failed, .cs.is-cancelled, .cs.is-expired { opacity: 0.85; }
.cs-top { display: flex; align-items: center; gap: var(--s-2); margin-bottom: var(--s-3); }
.cs-tile { width: 32px; height: 32px; flex: none; display: flex; align-items: center; justify-content: center; border-radius: 10px; color: var(--btn-primary-text); background: linear-gradient(135deg, var(--brand-orange), #fdba74); }
.cs-tile svg { width: 16px; height: 16px; fill: currentColor; stroke: currentColor; stroke-width: 1.5; stroke-linejoin: round; }
.cs-when { margin-left: auto; font-size: var(--t-caption); color: var(--text-muted); white-space: nowrap; }
.cs-title { font-size: 15px; font-weight: 650; letter-spacing: -0.01em; line-height: 1.4; color: var(--text-primary); }
.cs-sum, .cs-counts { margin-top: var(--s-1); font-size: var(--t-body-sm); line-height: 1.55; color: var(--text-secondary); white-space: pre-line; overflow-wrap: anywhere; }
.cs-counts { color: var(--text-muted); font-variant-numeric: tabular-nums; }
.cs-progress { display: flex; align-items: center; gap: var(--s-3); margin-top: var(--s-3); font-size: var(--t-caption); color: var(--text-muted); font-variant-numeric: tabular-nums; }
.cs-bar { flex: 1; min-width: 60px; height: 8px; border-radius: 99px; background: var(--bg-tertiary); overflow: hidden; }
.cs-bar i { display: block; height: 100%; border-radius: 99px; background: linear-gradient(90deg, var(--brand-orange), #fdba74); }
.cs-link { display: inline-block; margin-top: var(--s-3); padding: 0; border: 0; background: none; color: var(--text-link); font: inherit; font-size: var(--t-body-sm); font-weight: 600; cursor: pointer; }
.cs-link:hover { text-decoration: underline; }
.cs-review { display: flex; flex-direction: column; gap: var(--s-3); max-height: 320px; overflow-y: auto; margin-top: var(--s-3); padding: var(--s-3); background: var(--bg-secondary); border: 1px solid var(--border-light); border-radius: 12px; }
.cs-group + .cs-group { padding-top: var(--s-3); border-top: 1px solid var(--border-light); }
.cs-g-head { display: flex; flex-wrap: wrap; align-items: baseline; gap: 2px var(--s-2); font-size: var(--t-body-sm); color: var(--text-primary); }
.cs-g-head b { font-weight: 600; }
.cs-g-arrow { color: var(--text-secondary); }
.cs-g-n { margin-left: auto; font-size: var(--t-caption); color: var(--text-muted); }
.cs-stores { display: flex; flex-wrap: wrap; gap: 6px; margin-top: var(--s-2); }
.cs-store { padding: 2px 9px; border: 1px solid var(--border-primary); border-radius: 999px; background: var(--bg-primary); color: var(--text-secondary); font-size: var(--t-caption); line-height: var(--t-caption-lh); }
.cs-store.st-applied, .cs-store.st-done { border-color: transparent; background: var(--success-bg); color: var(--success); }
.cs-store.st-conflict, .cs-store.st-skipped { border-color: transparent; background: var(--warning-bg); color: var(--warning); }
.cs-store.st-failed { border-color: transparent; background: var(--danger-bg); color: var(--danger); }
.cs-note { font-size: var(--t-caption); line-height: 1.5; color: var(--text-muted); }
.cs-actions { display: flex; flex-wrap: wrap; gap: var(--s-2); margin-top: var(--s-4); }
.cs-btn { display: inline-flex; align-items: center; justify-content: center; min-height: 34px; padding: 6px 16px; border: 1px solid var(--border-primary); border-radius: 999px; background: var(--bg-primary); color: var(--text-secondary); font: inherit; font-size: var(--t-body-sm); font-weight: 600; line-height: 1.3; text-align: center; cursor: pointer; }
.cs-btn:hover:not(:disabled) { border-color: var(--brand-orange); color: var(--text-primary); }
.cs-btn:disabled { opacity: 0.45; cursor: default; }
.cs-btn--go { background: var(--btn-primary-bg); border-color: var(--btn-primary-bg); color: var(--btn-primary-text); }
.cs-btn--go:hover:not(:disabled) { background: var(--btn-primary-hover-bg); border-color: var(--btn-primary-hover-bg); color: var(--btn-primary-hover-text); }
</style>
